import {
  type Variants,
  motion,
  useInView,
  useReducedMotion,
} from 'motion/react';
import { useRef } from 'react';

const DX = 10;
const DY = 5;
const BLOCK_W = 40;
const BLOCK_H = 27;

interface BlockDef {
  w: number;
  h: number;
  x: number;
  y: number;
  front: string;
  top: string;
  side: string;
  label?: string;
  labelColor: string;
}

const INK = {
  front: '#1a1816',
  top: '#2d2b28',
  side: '#111010',
  labelColor: '#faf8f5',
};

function depthRow(z: number, count: number): BlockDef[] {
  return Array.from({ length: count }, (_, gx) => ({
    w: BLOCK_W,
    h: BLOCK_H,
    x: gx * BLOCK_W + z * DX,
    y: (5 - z) * DY,
    ...INK,
  }));
}

const LIGHT_A = {
  front: '#f3eeeb',
  top: '#f8f6f3',
  side: '#e9e5e1',
  labelColor: '#1a1816',
};

const LIGHT_B = {
  front: '#faf8f5',
  top: '#ffffff',
  side: '#f0ece8',
  labelColor: '#1a1816',
};

const COMMUNITY_LETTERS = ['C', 'O', 'M', 'M', 'U', 'N', 'I', 'T', 'Y'];

function communityRow(): BlockDef[] {
  return COMMUNITY_LETTERS.map((letter, i) => ({
    w: BLOCK_W,
    h: BLOCK_H,
    x: i * BLOCK_W,
    y: 5 * DY,
    ...(i % 2 === 0 ? LIGHT_A : LIGHT_B),
    label: letter,
  }));
}

const BLOCKS: BlockDef[] = [
  ...depthRow(5, 4),
  ...depthRow(4, 5),
  ...depthRow(3, 6),
  ...depthRow(2, 7),
  ...depthRow(1, 8),

  ...communityRow(),

  {
    w: 42,
    h: 25,
    x: 0,
    y: 5 * DY - 25,
    front: '#e84a00',
    top: '#f25a15',
    side: '#d04000',
    labelColor: '#ffffff',
  },

  {
    w: 60,
    h: 25,
    x: 80,
    y: 5 * DY - 25,
    ...INK,
    label: 'The',
  },
  {
    w: 80,
    h: 25,
    x: 140,
    y: 5 * DY - 25,
    front: '#e84a00',
    top: '#f25a15',
    side: '#d04000',
    label: 'Agent',
    labelColor: '#ffffff',
  },
  {
    w: 60,
    h: 25,
    x: 220,
    y: 5 * DY - 25,
    ...INK,
    label: 'Lab',
  },
];

const SCENE_W = 9 * BLOCK_W + DX;
const SCENE_H = 5 * DY + BLOCK_H + DY;

const containerVariants: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.03, delayChildren: 0.05 },
  },
};

const flyInVariants: Variants = {
  hidden: { x: 400, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: { type: 'spring', stiffness: 140, damping: 22 },
  },
};

function BlockSVG({ block }: { block: BlockDef }) {
  const { w, h, front, top, side, label, labelColor } = block;
  const svgW = w + DX;
  const svgH = h + DY;

  return (
    <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`}>
      <polygon
        points={`0,${DY} ${DX},0 ${w + DX},0 ${w},${DY}`}
        fill={top}
        stroke="#1a1816"
        strokeWidth="2"
      />
      <polygon
        points={`${w},${DY} ${w + DX},0 ${w + DX},${h} ${w},${DY + h}`}
        fill={side}
        stroke="#1a1816"
        strokeWidth="2"
      />
      <polygon
        points={`0,${DY} ${w},${DY} ${w},${DY + h} 0,${DY + h}`}
        fill={front}
        stroke="#1a1816"
        strokeWidth="2"
      />
      {label && (
        <text
          x={w / 2}
          y={DY + h / 2 + 4}
          textAnchor="middle"
          fontFamily="Inter, sans-serif"
          fontSize="12"
          fontWeight="400"
          fill={labelColor}
        >
          {label}
        </text>
      )}
    </svg>
  );
}

export function BlockAnimation() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const reduced = useReducedMotion();

  return (
    <motion.div
      ref={ref}
      className="relative mx-auto overflow-hidden"
      style={{ width: SCENE_W, height: SCENE_H }}
      aria-hidden="true"
      variants={containerVariants}
      initial={reduced ? 'visible' : 'hidden'}
      animate={reduced ? 'visible' : isInView ? 'visible' : 'hidden'}
    >
      {BLOCKS.map((block, i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{ left: block.x, top: block.y }}
          variants={flyInVariants}
        >
          <BlockSVG block={block} />
        </motion.div>
      ))}
    </motion.div>
  );
}
